/**
 * useAppSession — tracks app session duration and writes records to Firestore.
 *
 * A session starts when the authenticated layout mounts (or app returns to
 * foreground) and ends when the page becomes hidden or the user navigates away.
 * Each session record stored:
 *   { userId, startedAt, endedAt, durationSeconds, platform }
 *
 * Minimum session length: 10 seconds (filters out accidental taps/brief switches).
 * Works on web (visibilitychange + beforeunload) and native (Capacitor appStateChange).
 */

import { useEffect, useRef } from 'react';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db, COLLECTIONS } from '../utils/core/firebase-config';
import { isNative } from '../utils/core/platform';
import logger from '../utils/core/logger';

const MIN_SESSION_SECONDS = 10;

function getPlatform() {
  if (isNative()) {
    const ua = navigator.userAgent || '';
    return /iPad|iPhone|iPod/.test(ua) ? 'ios' : 'android';
  }
  return 'web';
}

export function useAppSession(userId) {
  const sessionStartRef = useRef(null);
  const platform = getPlatform();

  const flush = async (startTs) => {
    if (!userId || !startTs) return;
    const durationSeconds = Math.round((Date.now() - startTs) / 1000);
    if (durationSeconds < MIN_SESSION_SECONDS) return;

    try {
      await addDoc(collection(db, COLLECTIONS.APP_SESSIONS), {
        userId,
        startedAt: Timestamp.fromMillis(startTs),
        endedAt: Timestamp.now(),
        durationSeconds,
        platform,
      });
      logger.debug('[AppSession] Session recorded', { durationSeconds, platform });
    } catch (err) {
      logger.warn('[AppSession] Failed to write session', err);
    }
  };

  useEffect(() => {
    if (!userId) return;

    sessionStartRef.current = Date.now();

    // Web: visibilitychange covers tab switch, minimize, and most browser-close cases
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flush(sessionStartRef.current);
        sessionStartRef.current = null;
      } else if (document.visibilityState === 'visible' && !sessionStartRef.current) {
        sessionStartRef.current = Date.now();
      }
    };

    // beforeunload catches hard closes that visibilitychange misses (rare but real)
    const handleBeforeUnload = () => {
      flush(sessionStartRef.current);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Native: Capacitor fires appStateChange instead of visibilitychange
    let removeCapacitorListener = null;
    let mounted = true;
    if (isNative()) {
      import('@capacitor/app').then(({ App }) => {
        if (!mounted) return; // component unmounted before import resolved
        App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) {
            flush(sessionStartRef.current);
            sessionStartRef.current = null;
          } else if (!sessionStartRef.current) {
            sessionStartRef.current = Date.now();
          }
        }).then(handle => {
          if (!mounted) { handle?.remove(); return; }
          removeCapacitorListener = handle;
        });
      }).catch(() => {});
    }

    return () => {
      mounted = false;
      // Flush on unmount (user logs out, route changes away from protected layout)
      flush(sessionStartRef.current);
      sessionStartRef.current = null;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      removeCapacitorListener?.remove?.();
    };
  }, [userId]); // re-initialise if userId changes (sign-out → sign-in)
}
